<template>
  <Transition enter-from-class="opacity-0" leave-to-class="opacity-0">
    <RevBackdrop
      v-if="isVisible"
      class="bg-overlay-low z-20 transition-opacity duration-200 md:hidden"
    />
  </Transition>
  <Transition
    enter-active-class="transition-all duration-200 ease-out"
    enter-from-class="translate-y-full opacity-0 md:-translate-y-56"
    enter-to-class="translate-y-0 opacity-100 md:translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100 md:translate-y-0"
    leave-to-class="translate-y-full opacity-0 md:-translate-y-56"
  >
    <div
      v-if="isVisible"
      ref="atcPopover"
      :aria-describedby="hasError ? 'atc-popover-error-description' : undefined"
      aria-labelledby="atc-popover-title"
      aria-modal="true"
      class="bg-surface-default-low shadow-long rounded-t-lg fixed bottom-0 left-0 z-20 flex max-h-[90vh] w-full flex-col overflow-hidden p-16 transition-all md:rounded-lg md:bottom-auto md:left-auto md:right-24 md:top-[86px] md:max-h-[calc(100vh-96px)] md:w-[320px] lg:right-[calc((100vw-1120px)/2)]"
      :role="hasError ? 'alertdialog' : 'dialog'"
      tabindex="-1"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-x-8">
          <IconCheckInCircleFilled
            v-if="!hasError"
            class="text-static-success-hi shrink-0"
            size="20"
          />

          <p id="atc-popover-title" class="heading-3">
            {{
              hasError
                ? i18n(translations.errorTitle)
                : i18n(translations.successTitle)
            }}
          </p>
        </div>
        <RevButtonIcon
          :aria-label="i18n(translations.closeLabel)"
          class="shrink-0"
          :icon="IconCrossSmall"
          size="small"
          variant="ghost"
          @click="hide"
        />
      </div>

      <div
        v-if="hasError || product"
        class="divide-static-default-low shrink divide-y overflow-hidden overflow-y-auto"
        :class="hasError && errorBlocking ? 'mt-24' : 'mt-8'"
      >
        <div>
          <RevInfoBlock
            v-if="hasError"
            id="atc-popover-error-description"
            class="mb-16"
            :icon="IconWarning"
            :title="i18n(translations.errorSubtitle)"
            variant="danger"
          >
            <p class="body-2">
              {{ i18n(translations.errorMessage) }}
            </p>
          </RevInfoBlock>
          <ProductCardContent
            v-if="product && !(hasError && errorBlocking)"
            class="-mx-16"
            :options="{
              format: 'compact',
              withMarketingTags: false,
              withDeal: false,
              withWarranty: false,
              forceHideAddToCart: true,
            }"
            :product
          />
        </div>
        <div
          v-if="shouldShowRecommendations && !(hasError && errorBlocking)"
          class="mt-8 pt-24"
        >
          <p class="heading-2">{{ i18n(translations.goesWellWith) }}</p>
          <div
            v-if="recommendationsStatus === 'pending'"
            class="space-y-16 py-16"
          >
            <ProductCardSkeleton v-for="i in 2" :key="i" />
          </div>
          <div v-else>
            <ProductCardContent
              v-for="recommendation in recommendations"
              :key="recommendation.listingId"
              class="-mx-16"
              :options="{
                format: 'compact',
                withWarranty: false,
                addToCartSuccessMode: 'silent',
                addToCartErrorMode: 'inline',
              }"
              :product="recommendation"
            />
          </div>
        </div>
      </div>
      <RevButton
        class="mt-8 w-full"
        :to="{ name: 'checkout_cart', params: { locale } }"
        variant="primary"
      >
        {{ i18n(translations.goToCartButton) }}
      </RevButton>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevBackdrop } from '@ds/components/Backdrop'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconCheckInCircleFilled } from '@ds/icons/IconCheckInCircleFilled'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'
import { IconWarning } from '@ds/icons/IconWarning'
import { onKeyStroke, useFocusWithin } from '@vueuse/core'

import { useAddToCartPopover } from '../../composables/useAddToCartPopover'
import ProductCardContent from '../ProductCard/ProductCardContent.vue'

import translations from './AddToCartPopover.translations'
import ProductCardSkeleton from './components/ProductCardSkeleton.vue'

const route = useRoute()

const {
  isVisible,
  product,
  hasError,
  errorBlocking,
  hide,
  recommendations,
  recommendationsStatus,
} = useAddToCartPopover()
const locale = useI18nLocale()
const i18n = useI18n()
const atcPopoverRef = useTemplateRef('atcPopover')

const shouldShowRecommendations = computed(
  () =>
    recommendationsStatus.value === 'pending' ||
    recommendations.value.length > 0,
)

const { focused: isFocusedWithin } = useFocusWithin(atcPopoverRef)

onKeyStroke('Escape', () => {
  if (isVisible.value) {
    hide()
  }
})

watch(isVisible, (visible) => {
  if (visible) {
    setTimeout(() => {
      atcPopoverRef.value?.focus()
    }, 100)
  }
})

watch(isFocusedWithin, (focused) => {
  if (!focused && isVisible.value) {
    setTimeout(() => {
      if (!isFocusedWithin.value && isVisible.value) {
        hide()
      }
    }, 150)
  }
})

watch(
  () => route.name,
  () => {
    hide()
  },
)
</script>
